/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description EventTracker
 */
import { AxiosError } from 'axios';
import { PostHog } from 'posthog-js';

import config from 'configs/app';
import { getTenantName } from '@veritable/common/utils';
import { POSTHOG_GROUPS } from '@veritable/common/constants';

/**
 * @description Config
 */
type Config = EnvConfig['postHog'];

/**
 * @description PosthogEvent
 */
interface PosthogEvent {
	eventName: string;
	properties: AnyRecord;
}

/**
 * @description EventTracker
 */
class Tracker {
	config: Config;
	posthog: PostHog;
	// TODO : add grafana faro

	constructor(initConfig: Config) {
		this.config = initConfig;
		this.posthog = new PostHog();
	}

	public initialize = (user: App.User): void => {
		const tenant = getTenantName();
		this.posthog?.init(this.config.apiKey, this.config.config);
		this.posthog?.register_for_session({ release_version: RELEASE_VERSION });
		this.posthog?.identify(user.email, { email: user.email });
		this.posthog?.group(POSTHOG_GROUPS.tenant, tenant, { name: tenant });
	};

	public trackPageView = (documentTitle?: string): void => {
		const currentUrl = window.location.href;
		const titleToUse = documentTitle || document.title || 'Unknown Page';
		// PostHog
		this.posthog?.capture('$pageview', { $current_url: currentUrl, pageTitle: titleToUse });
	};

	public trackEvent = (event: PosthogEvent): void => {
		const posthogProperties = { ...event.properties, pageTitle: document.title || '' };
		this.posthog?.capture(event.eventName, posthogProperties);
	};

	public trackError = (error: AxiosError): void => {
		this.posthog?.capture('$exception', {
			message: error?.message || 'unknown',
			errorCode: error?.response?.status,
			$exception_list: [
				{
					type: 'Api Error',
					value: error?.message || 'unknown',
				},
			],
		});
	};
}

const EventTracker = new Tracker(config.postHog);
export default EventTracker;
